import { useSaveCustomFilter } from "$src/hooks/use-save-custom-filter";
import { datadogRum } from "$src/lib/datadog";
import { useAvailableFilters } from "$src/queries/use-available-filters";
import { omitBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import {
  dataRepresentation,
  date,
  dateComparisonPeriod,
  naturalNumber,
  questionType,
} from "@tracksuit/frontend/schemas";

import { useFunnelQuestions } from "../hooks/use-funnel-questions";
import { useRequiredDatesCount } from "../hooks/use-required-dates-count";
import { useShallowUpdateSearchParams } from "../hooks/use-shallow-update-search-params";
import { COMPETITOR_AVERAGE_ID } from "../lib/consts";
import { useAccount } from "./use-account";
import { useAdminSettings } from "./use-admin-settings";

type FiltersStore = {
  filters: z.infer<typeof filtersSchema>;
  ready: boolean;
  init(): void;
  set(filters: Partial<z.infer<typeof filtersSchema>>): void;
  reset(): void;
};

const demographicFilter = z.object({
  id: z.string(),
  filter: z.string(),
});
export type DemographicFilter = z.infer<typeof demographicFilter>;

export const filtersSchema = z.object({
  demographics: demographicFilter.array(),
  datePeriod: z.object({
    start: date.optional(),
    end: date.optional(),
    comparisonPeriod: dateComparisonPeriod.optional(),
  }),
  dateRange: z.object({
    start: date.optional(),
    end: date.optional(),
  }),
  question: questionType,
  questionList: questionType.array(),
  brandId: naturalNumber.optional(),
  brandIdList: naturalNumber.array(),
  dataRepresentation: dataRepresentation,
  hasNoPopulationData: z.boolean(),
});

const INITIAL_STATE: z.infer<typeof filtersSchema> = {
  demographics: [],
  datePeriod: {
    start: undefined,
    end: undefined,
    comparisonPeriod: "preceding" as const,
  },
  dateRange: {
    start: undefined,
    end: undefined,
  },
  question: "PROMPTED_AWARENESS",
  questionList: ["PROMPTED_AWARENESS", "CONSIDERATION", "USAGE", "PREFERENCE"],
  brandId: undefined,
  brandIdList: [],
  dataRepresentation: "percentage" as z.infer<typeof dataRepresentation>,
  hasNoPopulationData: false,
};

export const useFilters = createWithEqualityFn<FiltersStore>()(
  (set) => ({
    filters: INITIAL_STATE,
    ready: false,
    set(filters) {
      try {
        filtersSchema.partial().parse(filters);
        set((s) => ({
          ...s,
          filters: {
            ...s.filters,
            ...filters,
          },
        }));
      } catch (err) {
        datadogRum.addError(err);
      }
    },
    init() {
      set(() => ({ ready: true }));
    },
    reset() {
      set({ filters: INITIAL_STATE, ready: false });
    },
  }),
  shallow,
);

export const useInitFilters = () => {
  const account = useAccount((s) => s.active);
  const { availableFilters } = useAvailableFilters();
  const [setFilters, filtersReady, initFilters, resetFilters] = useFilters(
    (s) => [s.set, s.ready, s.init, s.reset],
  );
  const includeIncompleteMonths = useAdminSettings(
    (s) => s.settings.includeIncompleteMonths,
  );
  const funnelQuestions = useFunnelQuestions({
    unprompted: true,
    investigation: true,
    category: true,
  });
  const { sufficientDates } = useRequiredDatesCount(3);
  const defaultFilters = useMemo(() => {
    if (!availableFilters.dates || !account || !funnelQuestions) {
      return;
    }

    return {
      brandId: account?.brandId,
      brandIdList: [
        account?.brandId,
        COMPETITOR_AVERAGE_ID,
        ...(account?.competitors?.map((competitor) => competitor.id) ?? []),
      ],
      datePeriod: {
        start: sufficientDates
          ? availableFilters.dates[2]
          : availableFilters.dates[availableFilters.dates.length - 1],
        end: availableFilters.dates[0],
        comparisonPeriod: "preceding" as const,
      },
      questionList: funnelQuestions,
      dateRange: {
        start:
          availableFilters.dates[
            Math.min(availableFilters.dates.length, 12) - 1
          ],
        end: availableFilters.dates[0],
      },
    };
  }, [availableFilters.dates, funnelQuestions, account]);

  useEffect(() => {
    if (filtersReady || !defaultFilters) {
      return;
    }

    setFilters(defaultFilters);
    initFilters();
  }, [defaultFilters, filtersReady]);

  useEffect(() => {
    if (filtersReady && includeIncompleteMonths) {
      resetFilters();
    }
  }, [includeIncompleteMonths]);
};

export const useReflectFiltersToUrl = () => {
  const account = useAccount((s) => s.active);
  const { availableFilters } = useAvailableFilters();
  const funnelQuestions = useFunnelQuestions({
    investigation: true,
    unprompted: true,
    category: true,
  });
  const [searchParams] = useSearchParams();
  const { filters: customFilters } = useSaveCustomFilter();
  const [setFilters, filtersReady] = useFilters((s) => [s.set, s.ready]);
  const {
    demographics,
    dateRange,
    datePeriod,
    question,
    questionList,
    brandId,
    brandIdList,
    dataRepresentation,
  } = useFilters((s) => s.filters);
  const getDemographicFilterById = (id: string) => {
    const allFilters = availableFilters.demographics
      .map(({ filters }) => filters)
      .flat();
    return allFilters.find((f) => f.id === id);
  };
  const getDemographicObject = (filterIdOrLabel: string) => {
    return availableFilters?.demographics?.find((f: any) =>
      f.filters.some((filter: any) => {
        return (
          filter.id === filterIdOrLabel || filter.filter === filterIdOrLabel
        );
      }),
    );
  };

  const validateFilters = useCallback(
    (filters: Partial<z.infer<typeof filtersSchema>>) => {
      if (!account || !availableFilters) {
        return filters;
      }

      const brandIds = [
        account?.brandId,
        COMPETITOR_AVERAGE_ID,
        ...(account?.competitors?.map(({ id }) => id) ?? []),
      ];
      const validateField = (
        key: keyof typeof filters,
        validator: (filter: any) => boolean,
      ) => {
        if (!filters[key]) {
          return undefined;
        }

        return validator(filters[key]) ? filters[key] : undefined;
      };

      return omitBy(
        {
          ...filters,
          dataRepresentation: filters.dataRepresentation,
          brandId: validateField("brandId", (brandId) =>
            brandIds.includes(brandId),
          ),
          brandIdList: validateField("brandIdList", (brandIdList) =>
            brandIdList.every((id: any) => brandIds.includes(id)),
          ),
          questionList: validateField("questionList", (questionList) =>
            questionList.every((q: any) => funnelQuestions?.includes(q)),
          ),
          demographics: validateField("demographics", (demographics) =>
            demographics.every(({ id }: any) => !!getDemographicFilterById(id)),
          ),
          dateRange: validateField(
            "dateRange",
            (dateRange) =>
              !!availableFilters.dates?.includes(dateRange.start as string),
          ),
          datePeriod: validateField(
            "datePeriod",
            (datePeriod) =>
              !!availableFilters.dates?.includes(datePeriod.start as string),
          ),
        },
        (p) => (Array.isArray(p) ? !p.length : !p),
      ) as typeof filters;
    },
    [availableFilters, account],
  );

  const { pathname, state } = useLocation();
  const isInitialNavigation = useRef(true);
  const shouldUseCustomFilter = useMemo(() => {
    const useCustomFilter = state?.useCustomFilter;
    const navigationEntries = window.performance.getEntriesByType("navigation");
    const lastNavigation = navigationEntries[
      navigationEntries.length - 1
    ] as PerformanceNavigationTiming;
    const isReload =
      lastNavigation?.type === "reload" && isInitialNavigation.current;
    isInitialNavigation.current = false;

    return (useCustomFilter || isReload) && pathname.endsWith("/coachs-notes");
  }, [state, pathname]);

  const searchParamsFilters = useMemo(() => {
    if (!availableFilters.demographics) {
      return;
    }

    const filter = customFilters.find((filter) => filter.default);
    const demographics =
      filter && shouldUseCustomFilter
        ? filter.demographics
        : searchParams.getAll("demographics");

    const params = omitBy(
      {
        demographics: demographics?.map(getDemographicFilterById),
        question: searchParams.get("question"),
        dataRepresentation: searchParams.get("dataRepresentation"),
        brandId: Number(searchParams.get("brandId")),
        brandIdList: searchParams
          .getAll("brandIdList")
          ?.map((id) => Number(id)),
        questionList: searchParams.getAll("questionlist"),
        datePeriod:
          searchParams.get("periodStartDate") &&
          searchParams.get("periodEndDate")
            ? {
                start: searchParams.get("periodStartDate"),
                end: searchParams.get("periodEndDate"),
                comparisonPeriod:
                  searchParams.get("comparisonPeriod") ?? "preceding",
              }
            : undefined,
        dateRange:
          searchParams.get("rangeStartDate") && searchParams.get("rangeEndDate")
            ? {
                start: searchParams.get("rangeStartDate"),
                end: searchParams.get("rangeEndDate"),
              }
            : undefined,
        hasNoPopulationData: searchParams.getAll("demographics").length
          ? !searchParams
              .getAll("demographics")
              .every(
                (demographic) =>
                  getDemographicObject(demographic)?.supportsPopulation,
              )
          : false,
      },
      (p) => (Array.isArray(p) ? !p.length : !p),
    );
    const safeParams = filtersSchema.partial().safeParse(params);

    if (!safeParams.success) {
      console.warn(
        "search params include invalid filters: ",
        safeParams.error.issues,
      );
      datadogRum.addError(
        new Error(
          `search params include invalid filters: ${safeParams.error.issues
            .flat()
            .join(", ")}`,
        ),
      );
      return;
    }

    const validatedParams = validateFilters(safeParams.data);

    return validatedParams;
  }, [searchParams, availableFilters.demographics, shouldUseCustomFilter]);

  useEffect(() => {
    if (!filtersReady || !searchParamsFilters) {
      return;
    }

    setFilters(searchParamsFilters);
  }, [searchParamsFilters, filtersReady]);

  useShallowUpdateSearchParams(
    "demographics",
    demographics.map(({ id }) => id),
  );
  useShallowUpdateSearchParams("rangeStartDate", dateRange.start);
  useShallowUpdateSearchParams("rangeEndDate", dateRange.end);
  useShallowUpdateSearchParams("periodStartDate", datePeriod.start);
  useShallowUpdateSearchParams("periodEndDate", datePeriod.end);
  useShallowUpdateSearchParams("comparisonPeriod", datePeriod.comparisonPeriod);
  useShallowUpdateSearchParams("question", question);
  useShallowUpdateSearchParams("questionList", questionList);
  useShallowUpdateSearchParams("brandId", brandId);
  useShallowUpdateSearchParams(
    "brandIdList",
    brandIdList.map((id) => id),
  );
  useShallowUpdateSearchParams("dataRepresentation", dataRepresentation);
};
